import { useRef, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, GraduationCap, Highlighter, Monitor, Moon, Sun } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { THEMES, useTheme, type ThemePref } from '@/lib/theme';
import { cn } from '@/lib/cn';

export const THEME_ICON: Record<ThemePref, typeof Sun> = {
  system: Monitor, light: Sun, dark: Moon, fosforlu: Highlighter, menteem: GraduationCap,
};

/** i18n key of a look's name ("shell.themeFosforlu"). */
export const themeNameKey = (id: ThemePref) => `shell.theme${id.charAt(0).toUpperCase()}${id.slice(1)}`;

/** Where a click happened, for the look to grow from there (keyboard: the middle of the element). */
export function originOf(e: MouseEvent<HTMLElement>): { x: number; y: number } {
  if (e.clientX || e.clientY) return { x: e.clientX, y: e.clientY };
  const r = e.currentTarget.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

/** Three dots of a look (page, accent, highlight) for a menu row. */
export function ThemeSwatch({ id }: { id: ThemePref }) {
  if (id === 'system') {
    return (
      <span className="inline-flex overflow-hidden rounded-full border border-line" aria-hidden>
        <span className="size-3" data-theme-preview="light" style={{ background: 'var(--bg)' }} />
        <span className="size-3" data-theme-preview="dark" style={{ background: 'var(--bg)' }} />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 rounded-full border border-line px-0.5 py-0.5" data-theme-preview={id} aria-hidden style={{ background: 'var(--bg)' }}>
      <span className="size-2 rounded-full" style={{ background: 'var(--accent)' }} />
      <span className="size-2 rounded-full" style={{ background: id === 'fosforlu' ? 'var(--marker)' : id === 'menteem' ? 'var(--amber)' : 'var(--trusted)' }} />
    </span>
  );
}

/** A small drawing of the app in one look: the sidebar with the current page, a sheet with a heading, status pills and a button. */
function Miniature({ scope }: { scope: 'light' | 'dark' | 'fosforlu' | 'menteem' }) {
  return (
    <div data-theme-preview={scope} className="pv-page flex h-full min-h-0 w-full gap-1.5 p-1.5" style={{ backgroundColor: 'var(--bg)', color: 'var(--ink)', fontFamily: 'var(--font-body)' }}>
      <div className="flex w-[30%] flex-col gap-1 p-1" style={{ background: 'var(--surface)', borderRadius: 'var(--radius-ctl)', border: '1px solid var(--line)' }}>
        <span className="h-1.5 w-3/4 rounded-full" style={{ background: 'var(--ink)' }} />
        <span className="pv-nav-active mt-1 h-3 rounded-[3px]" />
        <span className="h-1 w-2/3 rounded-full" style={{ background: 'var(--line-strong)' }} />
        <span className="h-1 w-1/2 rounded-full" style={{ background: 'var(--line-strong)' }} />
      </div>
      <div className="pv-sheet flex min-w-0 flex-1 flex-col gap-1.5 p-2" style={{ backgroundColor: 'var(--surface)', borderRadius: 'var(--radius-panel)', border: '1px solid var(--line)' }}>
        <span className="truncate text-[13px] font-semibold leading-none" style={{ fontFamily: 'var(--font-display)' }}>Aa 1.0.0</span>
        <span className="flex gap-1">
          <span className="h-2.5 w-5 rounded-[3px]" style={{ background: 'var(--trusted-soft)', border: '1px solid var(--trusted)' }} />
          <span className="h-2.5 w-5 rounded-[3px]" style={{ background: 'var(--check-soft)', border: '1px solid var(--check)' }} />
          <span className="h-2.5 w-5 rounded-[3px]" style={{ background: 'var(--suspicious-soft)', border: '1px solid var(--suspicious)' }} />
        </span>
        <span className="mt-auto h-3.5 w-1/2 self-end" style={{ background: 'var(--accent)', borderRadius: 'var(--radius-ctl)' }} />
      </div>
    </div>
  );
}

function Preview({ id }: { id: ThemePref }) {
  if (id === 'system') {
    return (
      <div className="relative h-full w-full">
        <div className="absolute inset-0"><Miniature scope="light" /></div>
        <div className="absolute inset-0 [clip-path:polygon(62%_0,100%_0,100%_100%,38%_100%)]"><Miniature scope="dark" /></div>
      </div>
    );
  }
  return <Miniature scope={id} />;
}

/** Every look side by side, drawn in its own colours and type; picking one switches at once (and stays in this browser). */
export function AppearanceDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const { pref, setPref } = useTheme();
  // Where the pointer went down, so the new look grows from there; the keyboard switches without the effect.
  const origin = useRef<{ x: number; y: number } | null>(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg" title={t('appearance.title')} description={t('appearance.description')}>
      <div role="radiogroup" aria-label={t('appearance.title')} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="appearance-options">
        {THEMES.map(({ id }) => {
          const Icon = THEME_ICON[id];
          const active = pref === id;
          return (
            <label
              key={id}
              className={cn(
                'group relative flex cursor-pointer flex-col overflow-hidden rounded-[var(--radius-panel)] border bg-surface text-left transition-shadow',
                'has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent',
                active ? 'border-accent shadow-[0_0_0_1px_var(--accent)]' : 'border-line hover:border-line-strong',
              )}
              data-testid={`appearance-${id}`}
              onPointerDown={(e) => { origin.current = { x: e.clientX, y: e.clientY }; }}
            >
              <input
                type="radio"
                name="appearance"
                value={id}
                checked={active}
                className="sr-only"
                onChange={() => {
                  setPref(id, origin.current ?? undefined);
                  origin.current = null;
                }}
              />
              <span className="block aspect-[16/9] w-full border-b border-line" aria-hidden><Preview id={id} /></span>
              <span className="flex items-start gap-2 p-3">
                <Icon className="mt-0.5 size-4 shrink-0 text-ink-3" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">{t(themeNameKey(id))}</span>
                  <span className="block text-xs text-ink-2">{t(`appearance.desc.${id}`)}</span>
                </span>
                {active && <Check className="size-4 shrink-0 text-accent" aria-label={t('appearance.current')} />}
              </span>
            </label>
          );
        })}
      </div>
    </Dialog>
  );
}

export function ThemeLabel({ id, children }: { id: ThemePref; children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-2">
      <ThemeSwatch id={id} />
      {children ?? t(themeNameKey(id))}
    </span>
  );
}
